<template>
  <div class="upload-avatar">
    <!-- 点击区域触发文件选择 -->
    <div @click="triggerFileSelect" :style="!edit ? 'cursor: not-allowed' : 'cursor: pointer'">
      <div v-if="formData.type == 'headPic'">
        <img v-if="!formData.url" src="../../../../static/images/nan.jpg" alt="avatar" style="width: 135px; height: 146px" />
        <img v-else :src="formData.url" alt="avatar" style="width: 135px; height: 146px" />
      </div>
      <div v-else>
        <div v-if="!formData.url" class="upload-card" :class="{ 'upload-card-disabled': !edit }">
          <a-icon type="plus" />
          <div style="margin-top: 8px">Upload</div>
        </div>
        <div v-else class="img-container" :class="{ 'upload-card-disabled': !edit }">
          <img :src="formData.url" alt="img" />
        </div>
      </div>
    </div>
    <input type="file" ref="fileInput" :accept="accept" @change="onFileSelect" style="display: none" />

    <!-- 图片编辑弹窗 -->
    <a-modal
      v-model="editVisible"
      title="编辑图片"
      :width="800"
      :maskClosable="false"
      :closable="true"
      @cancel="cancelEdit"
      :destroyOnClose="true"
      :dialog-style="{ top: '60px' }"
    >
      <div class="cropper-wrapper" ref="cropperWrapper" style="height: 400px" v-if="editVisible">
        <VueCropper
          ref="cropper"
          :img="editImgSrc"
          :outputSize="1"
          :outputType="outputType"
          :info="true"
          :full="false"
          :canMove="true"
          :canMoveBox="true"
          :fixedBox="false"
          :original="false"
          :autoCrop="false"
          :centerBox="true"
          :high="true"
          :fixed="false"
          :fixedNumber="fixedNumber"
          :canScale="false"
          @imgLoad="onImgLoad"
        />
      </div>
      <div class="edit-toolbar">
        <a-button @click="rotateLeft">
          <a-icon type="undo" />
          左旋
        </a-button>
        <a-button @click="rotateRight">
          <a-icon type="redo" />
          右旋
        </a-button>
        <a-button @click="zoomIn">
          <a-icon type="zoom-in" />
          放大
        </a-button>
        <a-button @click="zoomOut">
          <a-icon type="zoom-out" />
          缩小
        </a-button>
        <a-button v-if="!hasCropped" @click="toggleCrop">
          <a-icon type="scissor" />
          裁剪
        </a-button>
        <a-button v-if="hasCropped" @click="cancelCrop">
          <a-icon type="close" />
          取消裁剪
        </a-button>
        <a-button v-if="hasCropped" type="primary" @click="confirmCrop">
          <a-icon type="check" />
          确定
        </a-button>
        <a-button @click="resetPreview">
          <a-icon type="reload" />
          重置
        </a-button>
      </div>
      <template slot="footer">
        <a-button @click="cancelEdit">取消</a-button>
        <a-button type="primary" @click="uploadImgClick" :loading="uploadLoading">上传</a-button>
      </template>
    </a-modal>
  </div>
</template>

<script>
import { VueCropper } from 'vue-cropper';

export default {
  name: 'JImageCutUpload',
  components: { VueCropper },
  model: {
    prop: 'value',
    event: 'change',
  },
  data() {
    return {
      headers: { 'X-Access-Token': localStorage.getItem('token') }, // 上传请求头
      accept: '.jpg,.png,.jpeg,.JPG,.PNG,.JPEG', // 允许上传文件类型
      uploadLoading: false, // 上传中状态
      selectedFile: null, // 当前选择的文件对象
      imgType: '', // 表单上传图片类型
      aspectRatio: '', // 表单上传图片规则
      edit: true, // 上传组件可用状态
      editVisible: false, // 编辑弹窗可见性
      editImgSrc: '', // 编辑预览图数据URL
      originalImgSrc: '', // 保存原始图片数据URL，用于重置还原
      outputType: 'jpeg', // 编辑弹框输出图片格式（jpeg/png）
      hasCropped: false, // 是否启用了裁剪
      pendingRestore: null, // refresh后待恢复的缩放/位置状态
    };
  },
  computed: {
    // 是否固定裁剪比例
    fixedCrop() {
      return !!this.aspectRatio && this.aspectRatio !== '';
    },
    // 裁剪框固定比例
    fixedNumber() {
      if (!this.fixedCrop) {
        return [0, 0];
      }
      switch (this.aspectRatio) {
        case 'width>height':
          return [4, 3];
        case 'height>width':
          return [3, 4];
        case 'square':
          return [1, 1];
        default:
          if (this.aspectRatio && this.aspectRatio.includes(':')) {
            return this.aspectRatio.split(':').map(Number);
          }
          return [1, 1];
      }
    },
  },
  props: {
    formData: {
      type: Object,
      required: true,
    },
    fileList: {
      type: Array,
      required: false,
    },
  },
  watch: {
    'formData.edit': {
      handler(val) {
        this.edit = val;
      },
      deep: true,
      immediate: true,
    },
    'formData.label': {
      handler(val) {
        if (val && val.includes('身份证') > 0) {
          this.imgType = 'IDCard';
          this.aspectRatio = 'width>height';
        }
      },
      deep: true,
      immediate: true,
    },
    // 监听弹窗可见性，自动绑定/解绑滚轮事件
    editVisible(val) {
      if (val) {
        this.$nextTick(() => {
          this.fitImageToView();
        });
      }
    },
  },
  beforeDestroy() {
    // 组件销毁前释放所有 blob URL，防止内存泄漏
    this.releaseBlobUrls();
  },
  methods: {
    /**
     * 点击上传区域触发隐藏的文件输入框，允许用户选择图片文件。
     */
    triggerFileSelect() {
      if (!this.edit) {
        return false;
      }
      this.$refs.fileInput.value = '';
      this.$refs.fileInput.click();
    },

    /**
     * 文件选择回调
     * @param e
     */
    onFileSelect(e) {
      const file = e.target.files[0];
      if (!file) {
        return false;
      }
      // 文件类型校验
      var fileType = file.type;
      if (fileType.indexOf('image') < 0) {
        this.$message.error('请上传图片类型文件');
        return false;
      }
      // 文件大小校验
      const isLt10M = file.size / 1024 / 1024;
      if (isLt10M > 10) {
        this.$message.error('图片大小不能超过10MB');
        return false;
      }

      // 打开编辑弹窗
      this.openEditDialog(file);
    },

    /**
     * 释放所有 blob URL，防止内存泄漏
     */
    releaseBlobUrls() {
      if (this.editImgSrc && this.editImgSrc.startsWith('blob:')) {
        URL.revokeObjectURL(this.editImgSrc);
      }
      if (this.originalImgSrc && this.originalImgSrc.startsWith('blob:')) {
        URL.revokeObjectURL(this.originalImgSrc);
      }
    },

    /**
     * 打开编辑弹窗
     * @param file
     */
    openEditDialog(file) {
      this.selectedFile = file;
      // 保存文件并确定输出格式
      this.outputType = file.type === 'image/png' ? 'png' : 'jpeg';

      // 读取文件并打开编辑弹窗
      const reader = new FileReader();
      reader.onload = ev => {
        this.editImgSrc = ev.target.result;
        this.originalImgSrc = ev.target.result;
        this.hasCropped = false;
        this.pendingRestore = null;
        this.editVisible = true;
      };
      reader.onerror = () => {
        this.$message.error('文件读取失败，请重试');
      };
      reader.readAsDataURL(file);
    },

    /**
     * 图片预览编辑--放大
     */
    zoomIn() {
      const cropper = this.$refs.cropper;
      if (!cropper) {
        return false;
      }
      const step = Math.max(0.1, cropper.scale * 0.1);
      const newScale = cropper.scale + step;
      if (newScale > 5) {
        return false;
      }
      cropper.scale = newScale;
      this.reCenterImage();
      this.adjustCropBoxToFit();
    },

    /**
     * 图片预览编辑--缩小
     */
    zoomOut() {
      const cropper = this.$refs.cropper;
      if (!cropper) {
        return false;
      }
      const step = Math.max(0.1, cropper.scale * 0.1);
      const newScale = cropper.scale - step;
      if (newScale < 0.1) {
        return false;
      }
      cropper.scale = newScale;
      this.reCenterImage();
      this.adjustCropBoxToFit();
    },

    /**
     * 图片预览编辑--左旋90度
     */
    rotateLeft() {
      this.$refs.cropper.rotateLeft();
      this.reCenterImage();
      this.adjustCropBoxToFit();
    },

    /**
     * 图片预览编辑--右旋90度
     */
    rotateRight() {
      this.$refs.cropper.rotateRight();
      this.reCenterImage();
      this.adjustCropBoxToFit();
    },

    /**
     * 重新居中图片（以预览框中心为基准）
     */
    reCenterImage() {
      this.$nextTick(() => {
        const cropper = this.$refs.cropper;
        if (!cropper) {
          return false;
        }
        const w = cropper.w;
        const h = cropper.h;
        const scale = cropper.scale;
        const trueWidth = cropper.trueWidth;
        const trueHeight = cropper.trueHeight;
        if (!trueWidth || !trueHeight) {
          return false;
        }
        // 与 vue-cropper 内部 reload 方法一致的居中公式
        cropper.x = -(trueWidth - trueWidth * scale) / 2 + (w - trueWidth * scale) / 2;
        cropper.y = -(trueHeight - trueHeight * scale) / 2 + (h - trueHeight * scale) / 2;
      });
    },

    /**
     * 裁切框自动调整：
     * 计算图片展示区域与预览框容器的交集，确保裁剪框始终不超出图片范围。
     * - 图片完整展示在预览框内 → 裁剪框 = 图片大小
     * - 图片缩放到超出预览框边界 → 裁剪框 = 图片与预览框的交集区域
     */
    adjustCropBoxToFit() {
      if (!this.hasCropped) {
        return false;
      }
      this.$nextTick(() => {
        const cropper = this.$refs.cropper;
        if (!cropper) {
          return false;
        }
        // 获取图片在容器中的边界坐标
        const imgAxis = cropper.getImgAxis();
        if (!imgAxis) return;
        const imgX1 = imgAxis.x1;
        const imgY1 = imgAxis.y1;
        const imgX2 = imgAxis.x2;
        const imgY2 = imgAxis.y2;
        const imgWidth = imgX2 - imgX1;
        const imgHeight = imgY2 - imgY1;
        if (imgWidth <= 0 || imgHeight <= 0) {
          return false;
        }

        // 容器尺寸
        const containerW = cropper.w;
        const containerH = cropper.h;

        // 计算图片与预览框容器的交集区域，确保裁剪框不超出图片范围
        const cropX = Math.max(0, imgX1);
        const cropY = Math.max(0, imgY1);
        const cropX2 = Math.min(containerW, imgX2);
        const cropY2 = Math.min(containerH, imgY2);

        let cropW = cropX2 - cropX;
        let cropH = cropY2 - cropY;
        if (cropW <= 0 || cropH <= 0) {
          return false;
        }

        cropper.cropW = cropW;
        cropper.cropH = cropH;
        cropper.cropOffsertX = cropX;
        cropper.cropOffsertY = cropY;
      });
    },

    /**
     * 开启裁剪模式
     * 图片小于预览框 → 裁切框包围图片
     * 图片超出预览框 → 裁切框在预览框范围内居中
     */
    toggleCrop() {
      const cropper = this.$refs.cropper;
      if (!cropper) {
        return false;
      }
      this.hasCropped = true;
      // goAutoCrop 是同步方法，仅初始化裁切框渲染，不改变 scale/x/y
      cropper.goAutoCrop();
      // 立即适配裁切框大小和位置
      this.adjustCropBoxToFit();
    },

    /**
     * 图片加载完成回调（由 vue-cropper imgLoad 事件触发）
     */
    onImgLoad(msg) {
      if (msg !== 'success') {
        return false;
      }
      if (!this.pendingRestore) {
        return false;
      }
      const cropper = this.$refs.cropper;
      if (!cropper) {
        return false;
      }
      const restore = this.pendingRestore;
      this.pendingRestore = null;

      if (restore.scale) {
        // 恢复缩放和位置（cancelCrop场景）
        if (restore.rotate !== undefined) {
          cropper.rotate = restore.rotate;
        }
        cropper.scale = restore.scale;
        this.$nextTick(() => {
          cropper.x = restore.x;
          cropper.y = restore.y;
          if (restore.needCropAdjust) {
            this.adjustCropBoxToFit();
          }
        });
      } else if (restore.needFit) {
        // 重新适配图片到预览框（confirmCrop/resetPreview场景）
        this.fitImageToView();
      }
    },

    /**
     * 取消裁剪：隐藏裁切框，保留当前缩放和位置
     */
    cancelCrop() {
      const cropper = this.$refs.cropper;
      if (!cropper) {
        return false;
      }
      // 保存当前状态，等 imgLoad 事件触发后恢复
      this.pendingRestore = {
        scale: cropper.scale,
        x: cropper.x,
        y: cropper.y,
        rotate: cropper.rotate,
        needCropAdjust: false,
      };
      this.hasCropped = false;
      cropper.refresh();
    },

    /**
     * 确定裁剪：将裁切结果替换为预览图
     */
    confirmCrop() {
      this.$refs.cropper.getCropBlob(blob => {
        // 释放旧的 URL（如果存在）
        if (this.editImgSrc && this.editImgSrc.startsWith('blob:')) {
          URL.revokeObjectURL(this.editImgSrc);
        }

        const url = URL.createObjectURL(blob);
        this.editImgSrc = url;
        this.hasCropped = false;
        // refresh后由imgLoad事件触发 fitImageToView
        this.pendingRestore = { needFit: true };
        this.$refs.cropper.refresh();
      });
    },

    /**
     * 重置预览（恢复到原始图片，关闭裁剪框）
     */
    resetPreview() {
      this.hasCropped = false;
      this.editImgSrc = this.originalImgSrc;
      // refresh后由imgLoad事件触发 fitImageToView
      this.pendingRestore = { needFit: true };
      this.$refs.cropper.refresh();
    },

    /**
     * 图片自适应预览框：原尺寸展示，超出边界则缩放至完整可见
     */
    fitImageToView() {
      const cropper = this.$refs.cropper;
      if (!cropper) {
        return false;
      }
      // vue-cropper 加载图片需要时间，延迟确保 trueWidth/trueHeight 已初始化
      const tryFit = () => {
        const { w, h, trueWidth, trueHeight } = cropper;
        if (!trueWidth || !trueHeight) {
          setTimeout(tryFit, 50);
          return false;
        }
        // 计算适配缩放比
        const fitScale = Math.min(w / trueWidth, h / trueHeight);
        // fitScale >= 1 说明图片原尺寸小于等于预览框，使用原尺寸(1)
        // fitScale < 1 说明图片超出预览框，缩放到完整可见
        const scale = fitScale >= 1 ? 1 : fitScale;
        cropper.scale = scale;
        this.$nextTick(() => {
          this.reCenterImage();
          // 仅在裁剪模式下适配裁切框
          if (this.hasCropped) {
            this.adjustCropBoxToFit();
          }
        });
      };
      tryFit();
    },

    /**
     * 取消编辑
     */
    cancelEdit() {
      this.editVisible = false;
      this.releaseBlobUrls();
      this.editImgSrc = '';
      this.originalImgSrc = '';
      this.selectedFile = null;
      this.hasCropped = false;
    },

    /**
     * 上传图片
     */
    uploadImgClick() {
      this.uploadLoading = true;
      this.$refs.cropper.getCropBlob(blob => {
        this.beforeUploadCheck(blob, true);
      });
    },

    /**
     * 上传前长宽比校验，校验通过后执行上传
     * @param fileOrBlob - 待上传的文件或Blob对象
     * @param isChange - 图片是否修改
     */
    beforeUploadCheck(fileOrBlob, isChange) {
      const imgRule = this.aspectRatio || '';
      // 无长宽比规则，直接上传
      if (!imgRule) {
        this.uploadSubmit(fileOrBlob, isChange);
        return;
      }
      const url = URL.createObjectURL(fileOrBlob);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        let isValid = false;
        let errorMsg = '';
        const imgHeight = img.height;
        const imgWidth = img.width;
        switch (imgRule) {
          case 'height>width':
            isValid = imgHeight > imgWidth;
            errorMsg = '图片高度必须大于宽度，请上传竖向拍摄的证件照';
            break;
          case 'width>height':
            isValid = imgWidth > imgHeight;
            errorMsg = '图片宽度必须大于高度，请上传横向拍摄的图片';
            break;
          case 'square':
            // 允许一定的误差范围（±5%）
            const ratio = imgWidth / imgHeight;
            isValid = ratio >= 0.95 && ratio <= 1.05;
            errorMsg = '图片必须是正方形';
            break;
          default:
            // 自定义比例，如 "16:9"
            if (imgRule.includes(':')) {
              const [targetWidth, targetHeight] = imgRule.split(':').map(Number);
              const actualRatio = imgWidth / imgHeight;
              const targetRatio = targetWidth / targetHeight;
              // 允许5%的误差
              isValid = Math.abs(actualRatio - targetRatio) / targetRatio <= 0.05;
              errorMsg = `图片长宽比必须是${targetWidth}:${targetHeight}`;
            } else {
              isValid = true;
            }
        }
        if (!isValid) {
          this.uploadLoading = false;
          this.$message.error(errorMsg);
          return;
        }
        this.uploadSubmit(fileOrBlob, isChange);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        this.uploadLoading = false;
        this.$message.error('图片加载失败，请重新选择');
      };
      img.src = url;
    },

    /**
     * 执行上传请求
     */
    uploadSubmit(fileOrBlob, isChange) {
      let file;
      if (isChange) {
        const ext = this.outputType === 'png' ? 'png' : 'jpg';
        const fileName = this.selectedFile ? this.selectedFile.name.replace(/\.\w+$/, '.' + ext) : 'cropped.' + ext;
        file = new File([fileOrBlob], fileName, { type: 'image/' + this.outputType });
      } else {
        file = fileOrBlob;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('imgType', this.imgType || '');

      const baseURL = this.axios.defaults.baseURL || '';
      const url = baseURL + '/biz/infocollect/uploadImg';

      this.axios
        .post(url, formData, { headers: this.headers })
        .then(res => {
          this.uploadLoading = false;
          if (res.success) {
            this.$message.success('上传成功!');
            this.$emit('change', {
              response: {
                result: res.result,
                success: true,
              },
            });
            this.$emit('getPath', res.result);
            // 清理弹窗资源并关闭
            this.releaseBlobUrls();
            this.editVisible = false;
            this.editImgSrc = '';
            this.originalImgSrc = '';
            this.selectedFile = null;
            this.hasCropped = false;
          } else {
            this.$message.error(res.message || '上传失败');
          }
        })
        .catch(() => {
          this.uploadLoading = false;
          this.$message.error('上传失败');
        });
    },
  },
};
</script>

<style scoped lang="css">
.upload-avatar >>> .ant-upload {
  padding: 0;
}
.upload-card {
  width: 104px;
  height: 104px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px dashed #d9d9d9;
  border-radius: 4px;
  transition: border-color 0.3s;
}
.upload-card:hover {
  border-color: #1890ff;
}
.upload-card-disabled,
.upload-card-disabled * {
  cursor: not-allowed !important;
}
.upload-card-disabled:hover {
  border-color: #d9d9d9 !important;
}
.img-container img {
  max-width: 300px;
  height: 150px;
}
.cropper-wrapper {
  width: 100%;
  min-height: 300px;
  overflow: hidden;
}
.edit-toolbar {
  margin-top: 12px;
  display: flex;
  gap: 8px;
  justify-content: center;
}
</style>



// WEBPACK FOOTER //
// src/pages/infocollect/components/JImageCutUpload.vue